<template>
  <o-modal :active="isActive" class="modal-sm" :closeOnEscape="true">
    <div class="modal-card">
      <header class="modal-card-head is-borderless">
        <p class="card-header-title is-justify-content-left has-text-black-bis">
          How to Use Search
        </p>
        <div class="is-divider-vertical"></div>
        <i class="icon-close is-clickable" @click="$emit('close')"></i>
      </header>
      <section class="modal-card-body line-height has-text-black-bis no-validate">
				<div class="has-text-weight-bold mb-3">Choose whether to search only the Order Name or across All Fields.</div>
				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						Order Name (default)
					</li>
					<div class="ml-5">
						<p>
							Looks only in order names.
						</p>
						Example: To find Steel Beam Delivery, search <b>steel</b> and/or <b>beam</b>.
					</div>
				</ul>

				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						All Fields
					</li>
					<div class="ml-5" v-if="!isProductionStatus">
						<p>
							Looks in {{ getAllFieldsByScreen }}.
						</p>
						Example: If the project is Tower A but the name doesn’t include it, search <b>Tower A</b> with <b>Order Details</b> selected.
					</div>
					<div v-else class="ml-5">
						<p>Looks in</p>
						<div class="ml-4">
							<p class="mb-2">Planning Module ('order name, description, tags, owner, item name, install location, project, todo assignee').</p>
							<p class="mb-2">Coordination Module ('order name, description, tags, owner, item name, install location, project, todo assignee ,lot/batch/release #, level, zone').</p>
							<p class="mb-2">Production Manager Module ('order name, description, tags, owner, item name, install location, project, todo assignee ,lot/batch/release #, level, zone, location, company, work step owner, workstep location, workstep run, work step owner, custom fields').</p>
							Example: If the project is Tower A but the name doesn’t include it, search Tower A with Order Details selected.
						</div>
					</div>
				</ul>
      	
				<div class="is-divider"></div>
				
				<div class="has-text-weight-bold mb-3">Search by query</div>
				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						Simple keywords
					</li>
					<div class="ml-5">
						<p>
							Type words separated by spaces to match orders containing those words.
						</p>
						<p>
							Example: Searching <b>steel beam</b> finds <b>Beam for Level 2</b> and <b>Steel Frame</b> because at least one words appear in the order names.
						</p>
					</div>
				</ul>

				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						Exact phrase
					</li>
					<div class="ml-5">
						Use double quotes to match a phrase exactly.
						<p>
							<b>"Steel beam"</b> matches when the name contains the exact phrase “steel beam”.
						</p>
						<p>
							Example: <b>"steel beam"</b> find <b>Cold Rolled Steel Beam assembly</b> because it contains keyword exactly matching
						</p>
					</div>
				</ul>

				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						Starts with
					</li>
					<div class="ml-5">
						<p>
							Use <b>“^Word"</b> to match values that start with that word.
						</p>
						<p>
							Example: <b>“^Fab”</b> matches <b>Fabricated Steel Beam</b> because it starts with “Fab”.
						</p>
					</div>
				</ul>
				
				<ul class="mb-3">
					<li class="ml-5 is-italic has-text-weight-bold">
						Must include all parts (+)
					</li>
					<div class="ml-5">
						<p class="mb-2">
							Use <b>+</b> to say, “must also include this”.
						</p>
						<ul class="circle-list ml-5">
							<li><b>“steel+beam”</b> → matches Steel Beam for Level 2 (contains both).</li>
							<li><b>“^Fab+beam”</b> → matches Fabricated Steel Beam (starts with “Fab” and includes “beam”).</li>
							<li><b>“^Fab+Shop”</b> → matches Fabrication Shop Drawings – Level 2 (starts with “Fab” and includes “Shop”).</li>
						</ul>
					</div>
				</ul>
      </section>
      <footer class="modal-card-foot"></footer>
    </div>
  </o-modal>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';

export default {
  props: {
    isActive: Boolean,
  },
  setup(props, { emit }) {
		const store = useStore();
		const getAllFieldsByScreen = computed(() => {
			const fieldsString = 'order name, description, tags, owner, item name, install location, project, todo assignee'
			const screenObjMap = {
				'planning': fieldsString,
				'coordination': fieldsString + ' ,lot/batch/release #, level, zone',
				'mm': fieldsString + ' ,delivery location, recipient name, vendor',
				'pm': fieldsString + ' ,lot/batch/release #, level, zone, location, company, work step owner, workstep location, workstep run, work step owner, custom fields',
				'shipping-order-view': 'order name, delivery location, current location, owner, receiver, item name, project, notify user, notes'
			}
			if (['mm-preparation', 'mm-sourcing', 'mm-ordering', 'material-status-order-view'].includes(store.state.activeScreen)) {
				return screenObjMap['mm']
			}
			if (['detailing', 'manufacturing', 'qa'].includes(store.state.activeScreen)) {
				return screenObjMap['pm']
			}
			return screenObjMap[store.state.activeScreen] || ''
		})

		const isProductionStatus = computed(() => {
			return store.state.activeScreen === 'production-status-order-view'
		})

		return {
			getAllFieldsByScreen,
			isProductionStatus
		}
	},
};

</script>

<style lang="scss" scoped>
	i.icon-close {
		display: inline !important;
	}
	ul {
		list-style: disc !important;
	}
	.circle-list {
		list-style: circle !important;
	}
	header,
	footer {
		background-color: #fff;
	}
	.modal-card-foot {
		height: 16px !important;
	}
	::v-deep(.o-modal__close) {
		display: none !important;
	}
	::v-deep(.o-modal__overlay) {
		opacity: 0.5;
	}
</style>
